/**
 * Cookie Consent Modal - Simil-R (Natif Shopify Customer Privacy API)
 * Gère les 3 étapes, les mascottes, la persuasion douce et la conformité RGPD.
 */

class CookieConsentModal extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'similr_cookie_consent_v1';
    this.cookieName = 'similr_cookie_consent';
    this.container = this.querySelector('[data-cookie-container]');
    this.backdrop = this.querySelector('[data-cookie-backdrop]');
    this.steps = this.querySelectorAll('[data-step]');
    this.reopenBtn = this.querySelector('[data-action="reopen-modal"]');
    this.delay = parseInt(this.dataset.delay || '1', 10) * 1000;
    this.isAdmin = this.dataset.isAdmin === 'true';

    this.init();
  }

  init() {
    this.initShopifyCustomerPrivacy();
    this.bindEvents();
    this.initThemeEditorEvents();
    this.exposeGlobalApi();

    const urlParams = new URLSearchParams(window.location.search);
    const isDebug = urlParams.has('cookie_debug') || urlParams.has('test_cookies');
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (isDebug) {
      setTimeout(() => this.openModal(1), 300);
      return;
    }

    // 1. Si déjà accepté : ne jamais ré-ouvrir automatiquement
    const savedConsent = this.getSavedConsent();
    if (savedConsent && savedConsent.accepted === true) {
      return;
    }

    // 2. Si pas accepté (refus explicite préalable) : max 1 fois par jour (24h), sauf en local
    const lastPrompt = this.getLastPromptTime();
    const oneDayMs = 24 * 60 * 60 * 1000;
    if (!isLocal && lastPrompt && Date.now() - lastPrompt < oneDayMs) {
      return;
    }

    // Afficher après le délai configuré
    setTimeout(() => {
      const current = this.getSavedConsent();
      if (!current || current.accepted !== true) {
        this.openModal(1);
      }
    }, this.delay);
  }

  exposeGlobalApi() {
    window.SimilrCookies = {
      hasAccepted: () => {
        const consent = this.getSavedConsent();
        return !!(consent && consent.accepted === true);
      },
      acceptAll: () => {
        this.handleAcceptAll();
      },
      openModal: (step = 1) => {
        this.openModal(step);
      },
    };
  }

  recordPromptTime() {
    const now = Date.now().toString();
    try {
      localStorage.setItem('similr_cookie_last_prompt', now);
    } catch (e) {}
    this.setCookie('similr_cookie_last_prompt', now, 1);
  }

  getLastPromptTime() {
    const cookieVal = this.getCookie('similr_cookie_last_prompt');
    if (cookieVal) {
      const parsed = parseInt(cookieVal, 10);
      if (!isNaN(parsed)) return parsed;
    }
    try {
      const lsVal = localStorage.getItem('similr_cookie_last_prompt');
      if (lsVal) {
        const parsed = parseInt(lsVal, 10);
        if (!isNaN(parsed)) return parsed;
      }
    } catch (e) {}
    return null;
  }

  /**
   * Événements spécifiques à l'éditeur de thème Shopify (Personnaliser)
   */
  initThemeEditorEvents() {
    if (!this.isAdmin) return;

    // Dans l'éditeur Shopify, ouvrir la modale SEULEMENT si le marchand clique sur la section
    document.addEventListener('shopify:section:select', (e) => {
      if (e.target.contains(this) || e.target === this.closest('.shopify-section')) {
        this.openModal(1);
      }
    });

    document.addEventListener('shopify:section:deselect', (e) => {
      if (e.target.contains(this) || e.target === this.closest('.shopify-section')) {
        this.closeModal();
      }
    });
  }

  /**
   * Initialisation de l'API Shopify Customer Privacy
   */
  initShopifyCustomerPrivacy() {
    if (window.Shopify && window.Shopify.loadFeatures) {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        (error) => {
          if (error) {
            console.warn('[Simil-R Cookies] Erreur consent-tracking-api:', error);
          } else {
            this.shopifyPrivacyReady = true;
            if (this.pendingShopifyPayload && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
              window.Shopify.customerPrivacy.setTrackingConsent(this.pendingShopifyPayload, () => {});
              this.pendingShopifyPayload = null;
            }
          }
        }
      );
    }
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      const actionBtn = e.target.closest('[data-action]');
      if (!actionBtn) return;

      const action = actionBtn.dataset.action;
      e.preventDefault();
      e.stopPropagation();

      switch (action) {
        case 'accept-all':
          this.handleAcceptAll();
          break;
        case 'go-step-1':
          this.goToStep(1);
          break;
        case 'go-step-2':
          this.goToStep(2);
          break;
        case 'go-step-3':
          this.goToStep(3);
          break;
        case 'save-preferences':
          this.handleSavePreferences();
          break;
        case 'confirm-refusal':
          this.handleConfirmRefusal();
          break;
        case 'reopen-modal':
          this.openModal(2);
          break;
      }
    });

    // Écouteur global pour rouvrir les réglages cookies via un lien (#cookies, data-open-cookie-modal)
    document.addEventListener('click', (e) => {
      const openTrigger = e.target.closest('[data-open-cookie-modal], a[href="#cookies"], a[href="#cookie-preferences"], [data-action="open-cookie-modal"]');
      if (openTrigger) {
        e.preventDefault();
        this.openModal(2);
      }
    });

    // Fermeture par clic sur le backdrop si déjà consenti (en mode ré-ouverture)
    if (this.backdrop) {
      this.backdrop.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.getSavedConsent()) {
          this.closeModal();
        }
      });
    }
  }

  goToStep(stepNumber) {
    this.steps.forEach((step) => {
      if (parseInt(step.dataset.step, 10) === stepNumber) {
        step.classList.add('cookie-modal__step--active');
      } else {
        step.classList.remove('cookie-modal__step--active');
      }
    });

    if (this.container) {
      this.container.scrollTop = 0;
    }
  }

  openModal(step = 1) {
    this.goToStep(step);
    this.classList.add('cookie-modal--open');
  }

  closeModal() {
    // 1. Mémoriser la position de lecture actuelle de la fenêtre
    const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;

    // 2. Retirer le focus du bouton cliqué pour éviter que le navigateur ne suive l'élément dans le footer
    if (document.activeElement && typeof document.activeElement.blur === 'function') {
      document.activeElement.blur();
    }
    if (typeof this.blur === 'function') {
      this.blur();
    }

    // 3. Fermer la modale
    this.classList.remove('cookie-modal--open');

    // 4. Verrouiller le scroll instantanément pour empêcher tout saut vers le bas
    window.scrollTo({
      top: currentScrollY,
      left: 0,
      behavior: 'instant'
    });

    requestAnimationFrame(() => {
      window.scrollTo(0, currentScrollY);
    });
  }

  handleAcceptAll() {
    const consent = {
      essential: true,
      preferences: true,
      analytics: true,
      marketing: true,
    };

    this.applyConsent(true, consent);
    this.closeModal();
  }

  handleSavePreferences() {
    const preferencesToggle = this.querySelector('#cookie-toggle-preferences');
    const analyticsToggle = this.querySelector('#cookie-toggle-analytics');
    const marketingToggle = this.querySelector('#cookie-toggle-marketing');

    const consent = {
      essential: true,
      preferences: preferencesToggle ? preferencesToggle.checked : true,
      analytics: analyticsToggle ? analyticsToggle.checked : true,
      marketing: marketingToggle ? marketingToggle.checked : true,
    };

    // Si tout est décoché, on dirige vers la fenêtre de dissuasion
    if (!consent.preferences && !consent.analytics && !consent.marketing) {
      this.goToStep(3);
      return;
    }

    const isFullAccepted = consent.preferences && consent.analytics && consent.marketing;
    this.applyConsent(isFullAccepted, consent);
    this.closeModal();
  }

  handleConfirmRefusal() {
    const consent = {
      essential: true,
      preferences: false,
      analytics: false,
      marketing: false,
    };

    this.applyConsent(false, consent);
    this.recordPromptTime();
    this.closeModal();
  }

  /**
   * Enregistre le consentement de manière triple (Cookie 1 an, LocalStorage, SessionStorage & Shopify API)
   */
  applyConsent(accepted, categories) {
    const consentRecord = {
      accepted: accepted,
      categories: categories,
      timestamp: new Date().toISOString(),
      version: '1.0',
    };

    // 1. Sauvegarde dans un cookie persistant 1 an (valable sur toutes les pages du domaine)
    this.setCookie(this.cookieName, JSON.stringify(consentRecord), 365);

    // 2. Sauvegarde LocalStorage
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(consentRecord));
    } catch (err) {
      console.warn('[Simil-R Cookies] LocalStorage inaccessible', err);
    }

    // 3. Sauvegarde SessionStorage
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(consentRecord));
    } catch (err) {}

    // 4. Appel à l'API Native Shopify Customer Privacy
    const shopifyPayload = {
      analytics: !!categories.analytics,
      marketing: !!categories.marketing,
      preferences: !!categories.preferences,
      sale_of_data: false,
    };

    if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
      try {
        window.Shopify.customerPrivacy.setTrackingConsent(shopifyPayload, () => {
          document.dispatchEvent(
            new CustomEvent('shopify:customerPrivacy:consentChanged', {
              detail: consentRecord,
            })
          );
        });
      } catch (err) {
        console.warn('[Simil-R Cookies] Erreur setTrackingConsent:', err);
      }
    } else {
      this.pendingShopifyPayload = shopifyPayload;
    }

    // Émettre un événement personnalisé global
    window.dispatchEvent(
      new CustomEvent('similr:cookieConsentUpdated', {
        detail: consentRecord,
      })
    );
  }

  /**
   * Vérifie si le consentement a déjà été donné via Cookie, LocalStorage ou Shopify
   */
  getSavedConsent() {
    // 1. Cookie HTTP persistant (prioritaire, cross-page garanti)
    const cookieVal = this.getCookie(this.cookieName);
    if (cookieVal) {
      try {
        return JSON.parse(cookieVal);
      } catch (e) {
        return { accepted: true };
      }
    }

    // 2. LocalStorage
    try {
      const lsItem = localStorage.getItem(this.storageKey);
      if (lsItem) return JSON.parse(lsItem);
    } catch (e) {}

    // 3. SessionStorage
    try {
      const ssItem = sessionStorage.getItem(this.storageKey);
      if (ssItem) return JSON.parse(ssItem);
    } catch (e) {}

    // 4. API Native Shopify
    if (window.Shopify && window.Shopify.customerPrivacy) {
      if (typeof window.Shopify.customerPrivacy.getTrackingConsent === 'function') {
        const consentState = window.Shopify.customerPrivacy.getTrackingConsent();
        if (consentState === 'yes' || consentState === 'no') {
          return { accepted: consentState === 'yes' };
        }
      }
    }

    return null;
  }

  /**
   * Helper gestion des cookies
   */
  setCookie(name, value, days) {
    let expires = '';
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toUTCString();
    }
    const encoded = encodeURIComponent(value);
    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encoded}${expires}; path=/; SameSite=Lax${secureFlag}`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) {
        try {
          return decodeURIComponent(c.substring(nameEQ.length));
        } catch (e) {
          return c.substring(nameEQ.length);
        }
      }
    }
    return null;
  }
}

if (!customElements.get('cookie-consent-modal')) {
  customElements.define('cookie-consent-modal', CookieConsentModal);
}
